import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Search, Close, Inventory2Outlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { OrderContext } from "../../contexts/OrderContext";
import OrderDetailsDialog from "./OrderDetailsDialog";
import {
  ACCENT,
  FOOTER_SPACE,
  PAGE_BG,
  STAGES,
  OrdersList,
  formatDate,
  sortNewestFirst,
} from "./orderUi";

// Customer's orders: track progress and repeat any earlier order.
const MyOrders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orders = {}, fetchOrders, loading } = useContext(OrderContext);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selected, setSelected] = useState(null);

  // Dashboard / footer links set ?status= (also while this page is open)
  useEffect(() => {
    setStatusFilter(
      new URLSearchParams(location.search).get("status") || "All",
    );
  }, [location.search]);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allOrders = useMemo(
    () => sortNewestFirst(Object.values(orders).flat().filter(Boolean)),
    [orders],
  );

  const counts = useMemo(() => {
    const result = { All: allOrders.length };
    allOrders.forEach((o) => {
      result[o.work_status] = (result[o.work_status] || 0) + 1;
    });
    return result;
  }, [allOrders]);

  // Only statuses that have orders (plus All and the current selection)
  const filterOptions = useMemo(() => {
    const extra = Object.keys(counts).filter(
      (s) => s !== "All" && !STAGES.includes(s),
    );
    return ["All", ...STAGES, ...extra].filter(
      (s) => s === "All" || counts[s] || s === statusFilter,
    );
  }, [counts, statusFilter]);

  const visibleOrders = useMemo(() => {
    const query = search.trim().toLowerCase();
    return allOrders.filter((o) => {
      if (statusFilter !== "All" && o.work_status !== statusFilter) {
        return false;
      }
      if (!query) return true;
      return [
        o.storeorderno,
        o.description,
        o.itemtype_name,
        o.design_name,
        formatDate(o.orderdate),
      ].some((v) => v && String(v).toLowerCase().includes(query));
    });
  }, [allOrders, search, statusFilter]);

  const handleReorder = (order) =>
    navigate("/order", { state: { reorderFrom: order } });

  const changeFilter = (status) =>
    navigate(
      status === "All"
        ? "/my-orders"
        : `/my-orders?status=${encodeURIComponent(status)}`,
      { replace: true },
    );

  return (
    <Box sx={{ bgcolor: PAGE_BG, minHeight: "100%", pb: FOOTER_SPACE }}>
      {/* Search + filters stay visible while scrolling */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          bgcolor: PAGE_BG,
          px: { xs: 2, md: 3 },
          pt: 1.5,
          pb: 1,
          borderBottom: "1px solid #E4E7EC",
        }}
      >
        <Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Search order no., item, notes or date"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <Close
                    fontSize="small"
                    sx={{ cursor: "pointer" }}
                    onClick={() => setSearch("")}
                  />
                </InputAdornment>
              ),
              sx: { bgcolor: "#fff", borderRadius: "8px" },
            }}
          />
          <Box
            sx={{
              display: "flex",
              gap: 1,
              overflowX: "auto",
              mt: 1,
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {filterOptions.map((status) => {
              const active = statusFilter === status;
              return (
                <Chip
                  key={status}
                  label={`${status} ${counts[status] || 0}`}
                  onClick={() => changeFilter(status)}
                  sx={{
                    flexShrink: 0,
                    fontWeight: 600,
                    bgcolor: active ? ACCENT : "#fff",
                    color: active ? "#fff" : "text.primary",
                    border: active ? "none" : "1px solid #E4E7EC",
                    "&:hover": { bgcolor: active ? ACCENT : "#F2F4F7" },
                  }}
                />
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, pt: 1.5 }}>
        {loading && allOrders.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CircularProgress sx={{ color: ACCENT }} />
          </Box>
        ) : visibleOrders.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
            <Inventory2Outlined sx={{ fontSize: 48, opacity: 0.4 }} />
            <Typography sx={{ mt: 1 }}>
              {allOrders.length === 0
                ? "You haven't placed any orders yet."
                : "No orders match your search."}
            </Typography>
            {allOrders.length === 0 && (
              <Button
                variant="contained"
                onClick={() => navigate("/order")}
                sx={{
                  mt: 2,
                  bgcolor: ACCENT,
                  textTransform: "none",
                  borderRadius: "8px",
                }}
              >
                Place your first order
              </Button>
            )}
          </Box>
        ) : (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              {visibleOrders.length} order
              {visibleOrders.length === 1 ? "" : "s"}
            </Typography>
            <OrdersList
              orders={visibleOrders}
              onOpen={setSelected}
              onReorder={handleReorder}
            />
          </>
        )}
      </Box>

      <OrderDetailsDialog
        order={selected}
        onClose={() => setSelected(null)}
        onReorder={handleReorder}
      />
    </Box>
  );
};

export default MyOrders;
